import React, { useState, useEffect, useCallback } from 'react';
import { getUserInfo, getAuthHeader } from '../utils/tokenManager';
import { ChevronLeftIcon, ChevronRightIcon, CalendarDaysIcon } from '@heroicons/react/24/outline';
import GroupInfoDrawer from '../components/GroupInfoDrawer';
import { getGroupSettlementMain, saveUserEvent, deleteUserEvent } from '../services/apiService';

const API_BASE_URL = 'https://trpliveapi1.tripbox.co.kr';

// 요일 이름
const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

// 월 이름
const MONTHS = ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'];

const ScheduleManagement = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [schedules, setSchedules] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [groupEvents, setGroupEvents] = useState([]);
  const [groupError, setGroupError] = useState(null);
  const [groupLoading, setGroupLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerSaleNo, setDrawerSaleNo] = useState(null);
  const [drawerTitle, setDrawerTitle] = useState('');
  const [expandedGroupDates, setExpandedGroupDates] = useState(new Set());
  const [newEventModalOpen, setNewEventModalOpen] = useState(false);
  const [newEventDate, setNewEventDate] = useState('');
  const [newEventTitle, setNewEventTitle] = useState('');
  const [newEventSaving, setNewEventSaving] = useState(false);
  const [newEventError, setNewEventError] = useState(null);
  const [editingEventId, setEditingEventId] = useState(null);

  // 현재 월의 시작일과 종료일 계산
  const getMonthRange = useCallback((date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    
    // 월의 첫째 날
    const firstDay = new Date(year, month, 1);
    // 월의 마지막 날
    const lastDay = new Date(year, month + 1, 0);
    
    // 캘린더 시작일 (첫째 주 일요일)
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - startDate.getDay());
    
    // 캘린더 종료일 (마지막 주 토요일)
    const endDate = new Date(lastDay);
    endDate.setDate(endDate.getDate() + (6 - endDate.getDay()));
    
    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
    };
  }, []);

  // 일정 데이터 로드
  const fetchSchedules = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const { companyNo, userNo } = getUserInfo() || {};
      if (!companyNo || !userNo) {
        setError('로그인 정보를 찾을 수 없습니다.');
        return;
      }

      const { startDate, endDate } = getMonthRange(currentDate);

      const payload = {
        Table1: [
          { 컬럼명: '@시작일', 값: startDate, 리스트값: null },
          { 컬럼명: '@종료일', 값: endDate, 리스트값: null },
          { 컬럼명: '@업체번호', 값: String(companyNo), 리스트값: null },
          { 컬럼명: '@유저번호', 값: String(userNo), 리스트값: null },
        ],
      };

      const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.CW_S_%EC%97%85%EC%B2%B4%EC%9D%BC%EC%A0%95%EA%B4%80%EB%A6%AC_TRP%EC%9A%A9`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();
      setSchedules(data?.table || []);

      // 행사단체 결산 메인 데이터도 같은 기간으로 조회
      try {
        setGroupLoading(true);
        setGroupError(null);
        const groups = await getGroupSettlementMain({
          startDate,
          endDate,
          searchBasis: '행사일',
          manager: 'NULL',
        });
        const list = Array.isArray(groups) ? groups : [];
        // 예약상태코드 필터: 049001, 049002, 049011, 049019 만 사용
        const allowedCodes = new Set(['049001', '049002', '049011', '049019']);
        const filtered = list.filter((row) => {
          const code = String(row?.예약상태코드 ?? '').trim();
          return allowedCodes.has(code);
        });
        setGroupEvents(filtered);
      } catch (e) {
        console.error('Failed to fetch group settlement main for schedule calendar', e);
        setGroupError(e?.message || '행사단체 정보를 불러오지 못했습니다.');
        setGroupEvents([]);
      } finally {
        setGroupLoading(false);
      }
    } catch (err) {
      console.error('Failed to fetch schedules:', err);
      setError('일정을 불러오는데 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  }, [currentDate, getMonthRange]);

  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);

  // 이전 달로 이동
  const goToPreviousMonth = () => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() - 1);
      return newDate;
    });
  };

  // 다음 달로 이동
  const goToNextMonth = () => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + 1);
      return newDate;
    });
  };

  // 오늘로 이동
  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // 캘린더 날짜 배열 생성
  const generateCalendarDays = useCallback(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - startDate.getDay());
    
    const days = [];
    const current = new Date(startDate);
    
    // 6주 (42일) 생성
    for (let i = 0; i < 42; i++) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    
    return days;
  }, [currentDate]);

  // 날짜를 YYYY-MM-DD 형식으로 변환 (로컬 시간 기준)
  const formatDateToLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // 행사단체 데이터를 날짜별로 매핑 (시작일 기준)
  const getGroupEventsForDate = useCallback((date) => {
    if (!Array.isArray(groupEvents) || groupEvents.length === 0) return [];
    const dateStr = formatDateToLocal(date);
    return groupEvents.filter((row) => {
      const raw = String(row?.시작일 || '').trim();
      if (!raw) return false;
      const onlyDate = raw.includes(' ') || raw.includes('T') ? raw.split(/[ T]/)[0] : raw;
      return onlyDate === dateStr;
    });
  }, [groupEvents]);

  // 특정 날짜의 일정 가져오기 (시작일~종료일 범위 내 모든 날짜에 표시)
  const getSchedulesForDate = useCallback((date) => {
    const dateStr = formatDateToLocal(date);
    return schedules.filter(schedule => {
      const startDate = schedule.시작일 || schedule.start?.split('T')[0];
      const endDate = schedule.종료일 || schedule.end?.split('T')[0] || startDate;
      
      // 날짜가 시작일~종료일 범위 내에 있는지 확인
      return dateStr >= startDate && dateStr <= endDate;
    });
  }, [schedules]);

  // 날짜가 현재 월인지 확인
  const isCurrentMonth = (date) => {
    return date.getMonth() === currentDate.getMonth();
  };

  // 오늘인지 확인
  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const calendarDays = generateCalendarDays();

  const toggleExpandedGroupsForDate = (dateStr) => {
    setExpandedGroupDates((prev) => {
      const next = new Set(prev);
      if (next.has(dateStr)) {
        next.delete(dateStr);
      } else {
        next.add(dateStr);
      }
      return next;
    });
  };

  return (
    <div className="min-h-screen space-y-4 p-4 md:p-6">
      {/* 헤더 */}
      <div className="rounded-2xl bg-white p-5">
        <div className="flex items-center justify-between">
         
          <div className="flex items-center space-x-2">
            <button
              onClick={goToToday}
              className="h-9 rounded-lg px-3.5 text-sm font-semibold border border-gray-300 bg-white text-gray-900 hover:bg-gray-50 transition-colors"
            >
              오늘
            </button>
            <div className="flex items-center space-x-1">
              <button
                onClick={goToPreviousMonth}
                className="flex size-9 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-900 transition-colors"
              >
                <ChevronLeftIcon className="w-5 h-5" />
              </button>
              <button
                onClick={goToNextMonth}
                className="flex size-9 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-900 transition-colors"
              >
                <ChevronRightIcon className="w-5 h-5" />
              </button>
            </div>
            <span className="text-[17px] font-bold text-gray-900 min-w-[120px] text-center">
              {currentDate.getFullYear()}년 {MONTHS[currentDate.getMonth()]}
            </span>
          </div>
        </div>
      </div>

      {/* 로딩/에러 상태 */}
      {isLoading && (
        <div className="flex items-center justify-center rounded-2xl bg-white py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600">일정을 불러오는 중...</span>
        </div>
      )}

      {error && (
        <div className="rounded-2xl bg-red-50 p-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {groupError && (
        <div className="rounded-2xl bg-amber-50 p-3">
          <p className="text-xs text-amber-700">{groupError}</p>
        </div>
      )}

      {/* 캘린더 */}
      {!isLoading && !error && (
        <div>
          <div className="rounded-2xl bg-white p-5">
            <div className="overflow-hidden rounded-xl border border-gray-200">
            {/* 요일 헤더 */}
            <div className="grid grid-cols-7 bg-gray-100 border-b border-gray-200">
              {WEEKDAYS.map((day, index) => (
                <div
                  key={day}
                  className={`py-2.5 text-center text-[13px] font-medium ${
                    index === 0 ? 'text-red-500' : index === 6 ? 'text-blue-500' : 'text-gray-500'
                  }`}
                >
                  {day}
                </div>
              ))}
            </div>

            {/* 날짜 그리드 */}
            <div className="grid grid-cols-7">
              {calendarDays.map((date, index) => {
                const daySchedules = getSchedulesForDate(date);
                const dayGroups = getGroupEventsForDate(date);
                const dateKey = formatDateToLocal(date);
                const isExpandedGroups = expandedGroupDates.has(dateKey);
                const totalCount = (daySchedules?.length || 0) + (dayGroups?.length || 0);
                const maxPreview = 3;
                let visibleSchedules = daySchedules || [];
                let visibleGroups = dayGroups || [];

                if (!isExpandedGroups) {
                  const maxSchedules = Math.min(visibleSchedules.length, maxPreview);
                  const remainingForGroups = Math.max(0, maxPreview - maxSchedules);
                  const maxGroups = Math.min(visibleGroups.length, remainingForGroups);
                  visibleSchedules = visibleSchedules.slice(0, maxSchedules);
                  visibleGroups = visibleGroups.slice(0, maxGroups);
                }

                const shownCount = (visibleSchedules?.length || 0) + (visibleGroups?.length || 0);
                const remainingCount = Math.max(0, totalCount - shownCount);
                const isInCurrentMonth = isCurrentMonth(date);
                const isTodayDate = isToday(date);
                const dayOfWeek = date.getDay();

                const handleDayClick = () => {
                  // 날짜 셀 빈 영역 클릭 시 신규 일정 추가 (단체/일정 항목은 stopPropagation으로 별도 처리)
                  setNewEventDate(dateKey);
                  setNewEventTitle('');
                  setNewEventError(null);
                  setEditingEventId(null);
                  setNewEventModalOpen(true);
                };

                return (
                  <div
                    key={index}
                    className={`min-h-[120px] border-b border-r border-gray-200 p-2 cursor-pointer hover:bg-gray-50 ${
                      !isInCurrentMonth ? 'bg-gray-50/70' : 'bg-white'
                    } ${index % 7 === 6 ? 'border-r-0' : ''}`}
                    onClick={handleDayClick}
                  >
                    {/* 날짜 숫자 */}
                    <div className="flex items-center justify-between mb-1">
                      <span
                        className={`inline-flex items-center justify-center w-7 h-7 text-sm font-medium rounded-full ${
                          isTodayDate
                            ? 'bg-blue-600 text-white'
                            : !isInCurrentMonth
                            ? 'text-gray-400'
                            : dayOfWeek === 0
                            ? 'text-red-500'
                            : dayOfWeek === 6
                            ? 'text-blue-500'
                            : 'text-gray-700'
                        }`}
                      >
                        {date.getDate()}
                      </span>
                    </div>

                    {/* 일정/행사 목록 */}
                    <div className="space-y-1 overflow-y-auto max-h-[128px]">
                      {visibleSchedules.map((schedule, idx) => (
                        <div
                          key={schedule.id || schedule.일정관리번호 || idx}
                          className="px-1.5 py-1 text-xs rounded-md truncate"
                          style={{
                            backgroundColor: schedule.color || schedule.색상 || '#E0E7FF',
                            color: getContrastColor(schedule.color || schedule.색상 || '#E0E7FF'),
                          }}
                          title={`${schedule.구분명 || ''} - ${schedule.일정명 || schedule.title || ''}`}
                          onClick={(e) => {
                            // 일정 바 클릭 시에는 날짜 셀 클릭(신규 일정 모달/단체 드로어)을 막고, 편집 모달을 연다
                            e.stopPropagation();
                            const eventId = schedule.일정관리번호 ?? schedule.id ?? null;
                            setEditingEventId(eventId);
                            setNewEventDate(dateKey);
                            setNewEventTitle(schedule.일정명 || schedule.title || '');
                            setNewEventError(null);
                            setNewEventModalOpen(true);
                          }}
                        >
                          <span className="font-medium">[{schedule.구분명 || '일정'}]</span>{' '}
                          {schedule.일정명 || schedule.title || ''}
                        </div>
                      ))}
                      {/* 행사단체 정보 (시작일 기준) */}
                      {visibleGroups && visibleGroups.length > 0 && (
                        <div className="mt-1 space-y-0.5">
                          {visibleGroups.map((grp, gIdx) => (
                            <div
                              key={gIdx}
                              className="px-1.5 py-0.5 text-[11px] rounded bg-blue-50 text-blue-700 truncate cursor-pointer hover:bg-blue-100"
                              title={`${grp.행사단체명 || ''} / 판매 ${grp.판매건수 ?? '-'}건 / 인원 ${grp.행사인원2 ?? grp.행사인원 ?? '-'}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                const saleNo = grp?.판매번호 ?? grp?.주문판매번호 ?? null;
                                if (!saleNo) return;
                                setDrawerSaleNo(saleNo);
                                setDrawerTitle(grp?.행사단체명 || '단체정보');
                                setDrawerOpen(true);
                              }}
                            >
                              <span className="font-semibold">{grp.행사단체명 || '행사단체'}</span>
                              <span className="ml-1 text-[10px]">
                                판매 {grp.판매건수 ?? '-'}건 · 인원 {grp.행사인원2 ?? grp.행사인원 ?? '-'}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                      {remainingCount > 0 && !isExpandedGroups && (
                        <button
                          type="button"
                          className="mt-0.5 text-[10px] text-blue-600 pl-1 underline underline-offset-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleExpandedGroupsForDate(dateKey);
                          }}
                        >
                          +{remainingCount}개 더보기
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            </div>
          </div>
        </div>
      )}

      {/* Group Info Drawer */}
      <GroupInfoDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        saleNo={drawerSaleNo}
        title={drawerTitle}
        onUpdated={fetchSchedules}
      />

      {/* 신규 일정 추가 / 편집 모달 */}
      {newEventModalOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
            <div className="flex items-start justify-between mb-3">
              <h2 className="text-[17px] font-bold text-gray-900">
                {editingEventId != null ? '일정 편집' : '신규 일정 추가'}
              </h2>
              <button
                type="button"
                className="flex size-8 items-center justify-center rounded-lg text-sm text-gray-400 hover:bg-gray-100"
                onClick={() => {
                  if (newEventSaving) return;
                  setNewEventModalOpen(false);
                  setNewEventTitle('');
                  setNewEventDate('');
                  setNewEventError(null);
                  setEditingEventId(null);
                }}
              >
                ✕
              </button>
            </div>
            <p className="text-xs text-gray-500 mb-2">선택한 날짜: {newEventDate}</p>
            <div className="space-y-2">
              <label className="block text-[13px] font-semibold text-gray-600">
                일정명
                <input
                  type="text"
                  className="mt-1 block w-full h-9 rounded-lg border border-gray-300 bg-white px-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30"
                  value={newEventTitle}
                  onChange={(e) => setNewEventTitle(e.target.value)}
                  placeholder="일정명을 입력하세요"
                />
              </label>
              {newEventError && (
                <p className="text-xs text-red-600 mt-1">{newEventError}</p>
              )}
            </div>
            <div className="mt-4 flex justify-end space-x-2">
              {editingEventId != null && (
                <button
                  type="button"
                  className="h-9 rounded-lg px-3.5 text-sm font-semibold bg-red-50 text-red-600 hover:bg-red-100"
                  disabled={newEventSaving}
                  onClick={async () => {
                    if (newEventSaving) return;
                    if (!editingEventId) return;
                    const confirmed = window.confirm('해당 일정을 삭제하시겠습니까?');
                    if (!confirmed) return;
                    try {
                      setNewEventSaving(true);
                      setNewEventError(null);
                      await deleteUserEvent({ eventNo: editingEventId });
                      setNewEventModalOpen(false);
                      setNewEventTitle('');
                      setNewEventDate('');
                      setEditingEventId(null);
                      await fetchSchedules();
                    } catch (e) {
                      setNewEventError(e?.message || '일정을 삭제하지 못했습니다.');
                    } finally {
                      setNewEventSaving(false);
                    }
                  }}
                >
                  삭제
                </button>
              )}
              <button
                type="button"
                className="h-9 rounded-lg px-3.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
                disabled={newEventSaving}
                onClick={async () => {
                  if (!newEventTitle.trim()) {
                    setNewEventError('일정명을 입력해 주세요.');
                    return;
                  }
                  if (!newEventDate) {
                    setNewEventError('선택된 날짜가 없습니다.');
                    return;
                  }
                  try {
                    setNewEventSaving(true);
                    setNewEventError(null);
                    await saveUserEvent({
                      date: newEventDate,
                      title: newEventTitle.trim(),
                      eventNo: editingEventId,
                    });
                    setNewEventModalOpen(false);
                    setNewEventTitle('');
                    setNewEventDate('');
                    setEditingEventId(null);
                    await fetchSchedules();
                  } catch (e) {
                    setNewEventError(e?.message || '일정을 저장하지 못했습니다.');
                  } finally {
                    setNewEventSaving(false);
                  }
                }}
              >
                {newEventSaving ? '저장 중...' : '저장'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// 배경색에 따른 텍스트 색상 결정 (대비를 위해)
const getContrastColor = (hexColor) => {
  if (!hexColor) return '#1F2937';
  
  // # 제거
  const hex = hexColor.replace('#', '');
  
  // RGB 값 추출
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  // 밝기 계산 (YIQ 공식)
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  
  return brightness > 128 ? '#1F2937' : '#FFFFFF';
};

export default ScheduleManagement;
